/**
 *  Toegankelijkheid van carousel-cellen.
 *
 *  Flickity zet zelf aria-hidden="true" op elke cel en haalt dat alleen weg op
 *  de geselecteerde cel. Bij een carousel waar meerdere cellen tegelijk in beeld
 *  staan, zoals de productcarousel, zijn de niet geselecteerde kaarten daardoor
 *  verborgen voor screenreaders en AI-agents terwijl de links en knoppen erin
 *  gewoon focusbaar blijven. Dat is de axe-fout "ARIA hidden element must not be
 *  focusable or contain focusable elements".
 *
 *  Deze module neemt het beheer van aria-hidden over, maar alleen voor de
 *  carousels die SlideShow zelf initialiseert. Andere Flickity-sliders in het
 *  thema (de PDP media-slider in mrk-product-slider.liquid, de sliders in
 *  app.js) blijven zich exact gedragen zoals nu.
 *
 *  Regels:
 *  - een cel die in beeld staat krijgt geen aria-hidden meer
 *  - een cel die echt buiten beeld staat krijgt aria-hidden EN inert, zodat er
 *    ook geen focusbare elementen meer in zitten
 *  - weten we het niet zeker, dan verbergen we niets. Niets verbergen kan de
 *    axe-fout nooit veroorzaken, iets ten onrechte verbergen wel
 *  - de class is-selected blijft precies zoals Flickity hem zet, daar hangt CSS
 *    aan (onder andere pointer-events en z-index bij fade)
 *
 *  Alles is fail-safe: ontbreken Flickity of Flickity.Cell, dan doet deze module
 *  helemaal niets en blijft het huidige gedrag staan. Er komt nooit een
 *  exception naar buiten die de Flickity-init of de rest van SlideShow raakt.
 */
const carouselCellA11y = (function () {

  const managed_roots = new WeakSet(),
    entries = [],
    edge_tolerance = 1,
    resize_delay = 200;
  let resize_bound = false,
    resize_timer = null;

  /* Hoort deze cel bij een carousel die wij beheren? */
  function isManagedCell(cell) {
    return !!(cell && cell.element && cell.parent && cell.parent.element &&
      managed_roots.has(cell.parent.element));
  }

  function isManagedCellSafe(cell) {
    try {
      return isManagedCell(cell);
    } catch (e) {
      return false;
    }
  }

  /* Flickity.Cell zo aanpassen dat Flickity zelf geen aria-hidden meer zet op
     onze cellen. is-selected blijft ongemoeid. Cellen van andere sliders houden
     exact het oude gedrag. */
  function patchCells() {
    if (typeof Flickity === 'undefined' || !Flickity || !Flickity.Cell || !Flickity.Cell.prototype) {
      return false;
    }
    const proto = Flickity.Cell.prototype;
    if (typeof proto.create !== 'function' || typeof proto.select !== 'function' ||
      typeof proto.unselect !== 'function') {
      return false;
    }
    /* De vlag staat op Flickity.Cell zelf. Vervangt een app later window.Flickity
       door een eigen build, dan wordt die nieuwe Cell alsnog netjes gepatcht. */
    if (Flickity.Cell.carouselCellA11yPatched) {
      return true;
    }
    const original_create = proto.create,
      original_destroy = proto.destroy,
      original_select = proto.select,
      original_unselect = proto.unselect;

    proto.create = function () {
      original_create.call(this);
      try {
        if (isManagedCell(this)) {
          this.element.removeAttribute('aria-hidden');
        }
      } catch (e) {
        /* stil, Flickity moet gewoon door kunnen */
      }
    };
    /* Alleen voor carousels die SlideShow zelf beheert wijken we af. Elke andere
       Flickity-slider op de pagina krijgt letterlijk het originele gedrag terug,
       zodat een latere Flickity-versie of een app-build niet stil overschreven wordt. */
    proto.select = function () {
      if (isManagedCellSafe(this)) {
        this.element.classList.add('is-selected');
        return;
      }
      original_select.call(this);
    };
    proto.unselect = function () {
      if (isManagedCellSafe(this)) {
        this.element.classList.remove('is-selected');
        return;
      }
      original_unselect.call(this);
    };
    if (typeof original_destroy === 'function') {
      /* Flickity haalt bij destroy zelf aria-hidden weg, inert moet er ook af */
      proto.destroy = function () {
        try {
          original_destroy.call(this);
        } finally {
          try {
            this.element.removeAttribute('inert');
          } catch (e) {
            /* stil */
          }
        }
      };
    }
    Flickity.Cell.carouselCellA11yPatched = true;
    return true;
  }

  /* Knipt dit element zijn inhoud af? Zo niet, dan staat een cel die buiten de
     viewport valt gewoon in beeld en mogen we hem nooit verbergen. In dit thema
     zet product-grid.css overflow op visible voor .products.carousel, en
     multicolumn.css en list-collections.css doen dat op mobiel. */
  function isClipping(element) {
    if (!element || typeof window.getComputedStyle !== 'function') {
      return false;
    }
    const style = window.getComputedStyle(element);
    if (!style) {
      return false;
    }
    const overflow_x = style.overflowX || style.overflow;
    return !!overflow_x && overflow_x !== 'visible';
  }

  /* Linker- en rechtergrens waarbinnen een cel zichtbaar is, of null als niets
     afknipt en we het dus niet kunnen bepalen. */
  function getClipBounds(flkty) {
    const boxes = [];
    let left = null,
      right = null;

    if (flkty.viewport && isClipping(flkty.viewport)) {
      boxes.push(flkty.viewport.getBoundingClientRect());
    }
    if (flkty.element && isClipping(flkty.element)) {
      boxes.push(flkty.element.getBoundingClientRect());
    }
    if (!boxes.length) {
      return null;
    }
    boxes.forEach((box) => {
      if (left === null || box.left > left) {
        left = box.left;
      }
      if (right === null || box.right < right) {
        right = box.right;
      }
    });
    if (!(right - left > 0)) {
      return null;
    }
    return {
      left: left,
      right: right
    };
  }

  /* Array met per cel true (in beeld) of false (buiten beeld), of null als we
     het niet zeker weten. */
  function getVisibility(flkty) {
    const cells = flkty.cells;

    if (!flkty.options) {
      return null;
    }
    if (flkty.options.fade) {
      /* Bij fade liggen alle cellen op elkaar, meten heeft geen zin. Alleen de
         geselecteerde slide is zichtbaar, de rest staat op opacity 0 en heeft al
         pointer-events: none via .flickity-enabled.is-fade in app.css. */
      let selected = flkty.selectedElements;
      if ((!selected || !selected.length) && flkty.selectedElement) {
        selected = [flkty.selectedElement];
      }
      if (!selected || !selected.length) {
        return null;
      }
      return cells.map((cell) => !!cell.element && selected.indexOf(cell.element) !== -1);
    }
    if (flkty.isAnimating || flkty.isDragging) {
      /* Midden in een beweging zeggen de posities niets, we wachten op settle */
      return null;
    }
    const bounds = getClipBounds(flkty);
    if (!bounds) {
      return null;
    }
    return cells.map((cell) => {
      if (!cell.element) {
        return true;
      }
      const rect = cell.element.getBoundingClientRect();
      if (!rect || !(rect.width > 0)) {
        return true;
      }
      return rect.right > bounds.left + edge_tolerance &&
        rect.left < bounds.right - edge_tolerance;
    });
  }

  /* Zet de status per cel. Geeft false terug als deze carousel uit de lijst mag. */
  function syncEntry(entry) {
    const flkty = entry.flkty;

    if (!flkty || !flkty.element || !flkty.cells) {
      /* instantie is echt weg, entry mag eruit */
      return false;
    }
    if (!flkty.cells.length || !document.documentElement.contains(flkty.element)) {
      /* nu even niets te doen, maar de carousel blijft in de lijst, anders kan een
         achtergebleven inert nooit meer opgeruimd worden */
      return true;
    }
    const cells = flkty.cells,
      visibility = flkty.isActive ? getVisibility(flkty) : null,
      focused = document.activeElement;

    for (let i = 0; i < cells.length; i++) {
      const element = cells[i].element;
      if (!element) {
        continue;
      }
      let hide = visibility ? visibility[i] === false : false;
      /* nooit de focus onder iemand vandaan halen */
      if (hide && focused && element.contains(focused)) {
        hide = false;
      }
      if (hide) {
        if (element.getAttribute('aria-hidden') !== 'true') {
          element.setAttribute('aria-hidden', 'true');
        }
        if (!element.hasAttribute('inert')) {
          element.setAttribute('inert', '');
        }
      } else {
        if (element.hasAttribute('aria-hidden')) {
          element.removeAttribute('aria-hidden');
        }
        if (element.hasAttribute('inert')) {
          element.removeAttribute('inert');
        }
      }
    }
    return true;
  }

  function removeEntry(entry) {
    const index = entries.indexOf(entry);
    if (index !== -1) {
      entries.splice(index, 1);
    }
  }

  function safeSync(entry) {
    try {
      if (syncEntry(entry) === false) {
        removeEntry(entry);
      }
    } catch (e) {
      /* een fout hier mag de carousel of de pagina nooit raken */
    }
  }

  function syncAll() {
    entries.slice().forEach((entry) => {
      safeSync(entry);
    });
  }

  function onViewportChange() {
    if (resize_timer) {
      clearTimeout(resize_timer);
    }
    resize_timer = setTimeout(() => {
      resize_timer = null;
      syncAll();
    }, resize_delay);
  }

  function bindViewportEvents() {
    if (resize_bound) {
      return;
    }
    resize_bound = true;
    window.addEventListener('resize', onViewportChange);
    if (document.readyState !== 'complete') {
      /* beelden en fonts kunnen de breedtes na init nog verschuiven */
      window.addEventListener('load', onViewportChange);
    }
  }

  /* Aanroepen VOOR new Flickity(), anders zet Cell.create nog aria-hidden neer */
  function claim(element) {
    try {
      if (!element || !patchCells()) {
        return;
      }
      managed_roots.add(element);
    } catch (e) {
      /* stil, zonder patch blijft simpelweg het oude gedrag staan */
    }
  }

  /* Aanroepen NA new Flickity() */
  function register(flkty) {
    try {
      /* managed_roots bevat alleen elementen waarvoor claim() de patch rond kreeg */
      if (!flkty || !flkty.element || !managed_roots.has(flkty.element)) {
        return;
      }
      for (let i = 0; i < entries.length; i++) {
        if (entries[i].flkty === flkty) {
          return;
        }
      }
      const entry = {
        flkty: flkty
      };
      entries.push(entry);
      bindViewportEvents();
      if (typeof flkty.on === 'function') {
        flkty.on('select', () => {
          safeSync(entry);
        });
        flkty.on('settle', () => {
          safeSync(entry);
        });
        flkty.on('resize', () => {
          safeSync(entry);
        });
      }
      safeSync(entry);
    } catch (e) {
      /* stil */
    }
  }

  return {
    claim: claim,
    register: register
  };

})();

/**
 *  @class
 *  @function SlideShow
 */
class SlideShow {
  constructor() {
    let _this = this,
      slideshows = document.querySelectorAll('.carousel');

    if (!slideshows) {
      return;
    }

    slideshows.forEach((slideshow) => {

 
      let dots = slideshow.dataset.dots === 'true',
        slideshow_slides = Array.from(slideshow.querySelectorAll('.carousel__slide')),
        autoplay = slideshow.dataset.autoplay == 'false' ? false : parseInt(slideshow.dataset.autoplay, 10),
        align = slideshow.dataset.align == 'center' ? 'center' : 'left',
        fade = slideshow.dataset.fade == 'true' ? true : false,
        prev_button = slideshow.querySelector('.flickity-prev'),
        next_button = slideshow.querySelector('.flickity-next'),
        custom_dots = slideshow.querySelector('.flickity-page-dots'),
        progress_bar = slideshow.parentNode.querySelector('.flickity-progress--bar'),
        animations = [],
        rightToLeft = document.dir === 'rtl',
        animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined',
        selectedIndex = 0,
        args = {
          wrapAround: true,
          cellAlign: align,
          pageDots: false,
          contain: true,
          fade: fade,
          autoPlay: autoplay,
          rightToLeft: rightToLeft,
          prevNextButtons: false,
          cellSelector: '.carousel__slide',
          on: {}
        };

      if (slideshow_slides.length < 1) {
        return;
      }
      if (slideshow.classList.contains('image-with-text-slideshow__image')) {
        let main_slideshow = slideshow.parentNode.querySelector('.image-with-text-slideshow__content'),
          image_slideshow_slides = slideshow.querySelectorAll('.image-with-text-slideshow__image-media');
        args.draggable = false;
        args.asNavFor = main_slideshow;

        if (image_slideshow_slides.length) {
          if (image_slideshow_slides[0].classList.contains('desktop-height-auto')) {
            args.adaptiveHeight = true;
          }
        }
      }
      if (slideshow.classList.contains('customer-reviews__image')) {
        let main_slideshow = slideshow.parentNode.querySelector('.customer-reviews__content');
        args.draggable = false;
        args.asNavFor = main_slideshow;
      }
      if (slideshow.classList.contains('image-with-text-slideshow__content') ||
        slideshow.classList.contains('testimonials__carousel') ||
        slideshow.classList.contains('customer-reviews__content') ||
        slideshow.classList.contains('customer-reviews__image')) {
        args.adaptiveHeight = true;
      }
      if (slideshow.classList.contains('custom-dots')) {

        if (animations_enabled && slideshow.classList.contains('main-slideshow')) {
          _this.prepareAnimations(slideshow, animations);
        }
        args.pauseAutoPlayOnHover = false;

        args.on = {
          staticClick: function () {
            this.unpausePlayer();
          },
          ready: function () {
            let flkty = this;
            // Animations.
            if (animations_enabled && slideshow.classList.contains('main-slideshow')) {
              _this.animateSlides(0, slideshow, animations);
            }

            // Custom Dots.
            if (dots && custom_dots) {
              let dots = custom_dots.querySelectorAll('li');
              dots.forEach((dot, i) => {
                dot.addEventListener('click', (e) => {
                  flkty.select(i);
                });
              });
              dots[this.selectedIndex].classList.add('is-selected');
            }
            document.fonts.ready.then(function () {
              flkty.resize();
            });

            // Video Support.
            let video_container = flkty.cells[0].element.querySelector('.slideshow__slide-video-bg');
            if (video_container) {

              if (video_container.querySelector('iframe')) {
                video_container.querySelector('iframe').onload = function () {
                  _this.videoPlay(video_container);
                };
              } else if (video_container.querySelector('video')) {
                video_container.querySelector('video').onloadstart = function () {
                  _this.videoPlay(video_container);
                };
              }
            }
          },
          change: function (index) {
            let previousIndex = fizzyUIUtils.modulo(this.selectedIndex - 1, this.slides.length);

            // Animations.
            if (animations_enabled && slideshow.classList.contains('main-slideshow')) {
              setTimeout(() => {
                _this.animateReverse(previousIndex, slideshow, animations);
              }, 300);
              _this.animateSlides(index, slideshow, animations);
            }

            // Custom Dots.
            if (dots && custom_dots) {
              let dots = custom_dots.querySelectorAll('li');
              dots.forEach((dot, i) => {
                dot.classList.remove('is-selected');
              });
              dots[this.selectedIndex].classList.add('is-selected');
            }

            // AutoPlay
            if (autoplay) {
              this.stopPlayer();
              this.playPlayer();
            }

            // Video Support.
            // previous slide
            let video_container_prev = flkty.cells[previousIndex].element.querySelector('.slideshow__slide-video-bg');
            if (video_container_prev) {
              _this.videoPause(video_container_prev);
            }
            // current slide
            let video_container = flkty.cells[index].element.querySelector('.slideshow__slide-video-bg');
            if (video_container) {
              if (video_container.querySelector('iframe')) {
                if (video_container.querySelector('iframe').classList.contains('lazyload')) {
                  video_container.querySelector('iframe').addEventListener('lazybeforeunveil', _this.videoPlay(video_container));
                  lazySizes.loader.checkElems();
                } else {
                  _this.videoPlay(video_container);
                }
              } else if (video_container.querySelector('video')) {
                _this.videoPlay(video_container);
              }
            }

          }
        };
      }
      if (slideshow.classList.contains('main-slideshow')) {
        if (slideshow.classList.contains('desktop-height-image') || slideshow.classList.contains('mobile-height-image')) {
          args.adaptiveHeight = true;
        }
      }
      if (slideshow.classList.contains('products')) {
        args.wrapAround = false;
        args.on.ready = function () {
          var flickity = this;
          if (next_button) {
            window.addEventListener('resize.center_arrows', function () {
              _this.centerArrows(flickity, slideshow, prev_button, next_button);
            });
          }
          window.dispatchEvent(new Event('resize.center_arrows'));
        };
      }
      if (progress_bar) {
        args.wrapAround = false;
        args.on.scroll = function (progress) {
          progress = Math.max(0, Math.min(1, progress));

          progress_bar.style.width = progress * 100 + '%';

        };
      }
      carouselCellA11y.claim(slideshow);
      const flkty = new Flickity(slideshow, args);
      carouselCellA11y.register(flkty);

      selectedIndex = flkty.selectedIndex;

      slideshow.dataset.initiated = true;


      if (prev_button) {
        prev_button.addEventListener('click', (event) => {
          flkty.previous();
        });
        prev_button.addEventListener('keyup', (event) => {
          flkty.previous();
        });
        next_button.addEventListener('click', (event) => {
          flkty.next();
        });
        next_button.addEventListener('keyup', (event) => {
          flkty.next();
        });
      }
      if (Shopify.designMode) {
        slideshow.addEventListener('shopify:block:select', (event) => {
          let index = slideshow_slides.indexOf(event.target);
          flkty.select(index);
        });
      }
    });

  }
  videoPause(video_container) {
    setTimeout(() => {
      if (video_container.dataset.provider === 'hosted') {
        video_container.querySelector('video').pause();
      } else if (video_container.dataset.provider === 'youtube') {
        video_container.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
          event: "command",
          func: "pauseVideo",
          args: ""
        }), "*");
      } else if (video_container.dataset.provider === 'vimeo') {
        video_container.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
          method: "pause"
        }), "*");
      }
    }, 10);
  }
  videoPlay(video_container) {
    setTimeout(() => {
      if (video_container.dataset.provider === 'hosted') {
        video_container.querySelector('video').play();
      } else if (video_container.dataset.provider === 'youtube') {
        video_container.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
          event: "command",
          func: "playVideo",
          args: ""
        }), "*");
      } else if (video_container.dataset.provider === 'vimeo') {
        video_container.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
          method: "play"
        }), "*");
      }
    }, 10);
  }
  prepareAnimations(slideshow, animations) {
    if (!slideshow.dataset.animationsReady) {
      new SplitText(slideshow.querySelectorAll('h1, p:not(.subheading)'), {
        type: 'lines, words',
        linesClass: 'line-child'
      });
      slideshow.querySelectorAll('.slideshow__slide').forEach((item, i) => {
        let tl = gsap.timeline({
          paused: true
        }),
          button_offset = 0;


        animations[i] = tl;

        if (slideshow.dataset.transition == 'swipe') {
          tl
            .to(item, {
              duration: 0.7,
              clipPath: "polygon(100% 0, 0 0, 0 100%, 100% 100%)"
            }, "start");
        }
        tl
          .to(item.querySelector('.slideshow__slide-bg'), {
            duration: 1.5,
            scale: 1
          }, "start");

        if (item.querySelector('.subheading')) {
          tl
            .fromTo(item.querySelector('.subheading'), {
              opacity: 0
            }, {
              duration: 0.5,
              opacity: 1
            }, 0);

          button_offset += 0.5;
        }
        if (item.querySelector('h1')) {
          let h1_duration = 0.5 + ((item.querySelectorAll('h1 .line-child div').length - 1) * 0.05);
          tl
            .from(item.querySelectorAll('h1 .line-child div'), {
              duration: h1_duration,
              yPercent: '100',
              stagger: 0.05
            }, 0);
          button_offset += h1_duration;
        }
        if (item.querySelector('p:not(.subheading)')) {

          let p_duration = 0.5 + ((item.querySelectorAll('p:not(.subheading) .line-child div').length - 1) * 0.02);
          tl
            .from(item.querySelectorAll('p:not(.subheading) .line-child div'), {
              duration: p_duration,
              yPercent: '100',
              stagger: 0.02
            }, 0);
          button_offset += p_duration;
        }
        if (item.querySelectorAll('.button')) {
          tl
            .fromTo(item.querySelectorAll('.button'), {
              y: '100%'
            }, {
              duration: 0.5,
              y: '0%',
              stagger: 0.1,
            }, button_offset * 0.2);
        }
        item.dataset.timeline = tl;
      });
      slideshow.dataset.animationsReady = true;
    }
  }
  animateSlides(i, slideshow, animations) {
    let flkty = Flickity.data(slideshow),
      active_slide = flkty.selectedElement;
    document.fonts.ready.then(function () {
      animations[i].timeScale(1).restart();
    });
  }
  animateReverse(i, slideshow, animations) {
    animations[i].timeScale(3).reverse();
  }
  centerArrows(flickity, slideshow, prev_button, next_button) {
    let first_cell = flickity.cells[0],
      max_height = 0,

      image_height = first_cell.element.querySelector('.product-featured-image').clientHeight;

    flickity.cells.forEach((item, i) => {
      if (item.size.height > max_height) {
        max_height = item.size.height;
      }
    });

    if (max_height > image_height) {
      let difference = (max_height - image_height) / -2;

      prev_button.style.transform = 'translateY(' + difference + 'px)';
      next_button.style.transform = 'translateY(' + difference + 'px)';
    }
  }
}
if (typeof SlideShow !== 'undefined') {
  new SlideShow();
}